<template>
<div :class="['listServices', { page: !embedded }]">
  <div v-if="!embedded" class="d-flex align-center flex-wrap pb-2 mb-2" style="gap: 12px">
    <h1 class="font-lg">Каталог услуг</h1>
  </div>

  <div class="d-flex flex-wrap align-center mb-4" style="gap: 12px">
    <v-btn-toggle v-if="hasKinds && !notBoughtOnly" v-model="kind" mandatory dense color="primary">
      <v-btn small value="tests">Тесты</v-btn>
      <v-btn small value="other">Другие услуги</v-btn>
    </v-btn-toggle>
    <v-btn-toggle v-if="!isAdmin && !notBoughtOnly" v-model="boughtFilter" mandatory dense color="primary">
      <v-btn small value="all">Все</v-btn>
      <v-btn small value="bought">Купленные</v-btn>
      <v-btn small value="not_bought">Не купленные</v-btn>
    </v-btn-toggle>
    <v-text-field v-model="search" label="Поиск" append-icon="mdi-magnify" outlined dense hide-details clearable style="max-width: 260px" />
  </div>

  <div class="d-flex gap-4">
    <div class="catalog-main">
      <div v-if="loadingAny && !tiles.length" class="tiles-grid">
        <v-skeleton-loader v-for="n in 6" :key="n" type="image, article" />
      </div>
      <StateBlock v-else-if="!visibleTiles.length" state="empty" title="Услуг не найдено"
        :text="search ? 'Попробуйте изменить запрос.' : 'В этом разделе пока нет услуг.'" />
      <template v-else>
        <section v-for="section in sections" :key="section.key" class="mb-6">
          <h2 v-if="section.title" class="tiles-title">{{ section.title }}</h2>
          <div class="tiles-grid">
            <ServiceTile
              v-for="tile in section.tiles"
              :key="tile.card.id"
              :tile="tile"
              :can-buy="canBuy"
              :busy="addingId === tile.card.id"
              @open="goToDetail"
              @add-to-cart="addToCart"
            />
          </div>
        </section>
      </template>
      <div v-if="stateHint && !embedded" class="caption grey--text text--darken-1 mt-4">{{ stateHint }}</div>
    </div>

    <!-- Баннер справа временно скрыт по решению бизнеса: карточки на всю ширину
    <div v-if="currentBanner.isUpload" class="banner_preview d-none d-lg-block">
      <BannerPreview :uploadFile="currentBanner" @clickBanner="showModalContact = !showModalContact"/>
    </div> -->
  </div>

  <ModalContactManager
    :showModalContact="showModalContact"
    @acceptSend="goToLink"
    @closeModal="showModalContact = false"
  />
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import api from '@/common/api';
// import BannerPreview from '@/components/admin-panel/BannerPreview.vue'; // баннер временно скрыт
import ServiceTile from '@/components/list-services/ServiceTile.vue';
import ModalContactManager from '@/components/list-services/ModalContactManager.vue';
import StateBlock from '@/components/ui/StateBlock.vue';
import { ADMIN, CORPCLIENT, MANAGER } from '@/common/constants/userTypes';
import { walletPeriodRange } from '@/components/clients/card/walletPeriods';
import {
  OWN_STATES, STATE_ORDER, loadOrderedCardIds, walletBalanceOf, tileState,
} from '@/components/list-services/catalogHelpers';

export default {
  name: 'listServices',
  metaInfo: { title: 'Каталог услуг' },
  props: {
    // Внутри кабинета менеджера: без заголовка страницы и её полей
    embedded: { type: Boolean, default: false },
    // Только то, что компания не покупала (для обзора менеджера: купленное он видит в таблице остатков)
    notBoughtOnly: { type: Boolean, default: false },
  },
  components: {
    ModalContactManager,
    ServiceTile,
    // BannerPreview,
    StateBlock,
  },
  data() {
    return {
      showModalContact: false,
      kind: 'tests',
      boughtFilter: this.notBoughtOnly ? 'not_bought' : 'all',
      search: '',
      // id карточек каталога из заказов пользователя
      paidCardIds: [],
      pendingCardIds: [],
      loadingOrders: false,
      loadingBalances: false,
      // Остатки: у корпа — сводка кошелька (по nomenclature_id), у менеджера — get-stock-balance (по code)
      managerStock: null,
      nomenclatureCodes: {},
      addingId: null,
    };
  },
  computed: {
    ...mapGetters({
      bannerLinks: 'users/admins/hasBannerLinks',
      user: 'auth/user',
      services: 'services/services/GET_ALL_SERVICES',
      servicesLoading: 'services/services/GET_LOADING_SERVICES',
      itemInBasket: 'basket/basket/itemInBasket',
      wallet: 'users/clients/wallet',
    }),
    userType() {
      return this.user ? this.user.user_type : null;
    },
    isAdmin() {
      return this.userType === ADMIN;
    },
    isCorp() {
      return this.userType === CORPCLIENT;
    },
    isManager() {
      return this.userType === MANAGER;
    },
    // Покупают корп и менеджер (решение бизнеса); админ каталог только смотрит
    canBuy() {
      return this.isCorp || this.isManager;
    },
    loadingAny() {
      return this.servicesLoading || this.loadingOrders || this.loadingBalances;
    },
    currentBanner() {
      return {
        isUpload: !!this.bannerLinks,
        image: this.bannerLinks ? this.bannerLinks : null,
      };
    },
    publishedCards() {
      return (this.services || []).filter((s) => s.status === 'published' && s.is_visible !== false);
    },
    tiles() {
      return this.publishedCards.map((card) => this.buildTile(card));
    },
    // Карточки каталога связаны с тестами (поле nomenclature заполнено в админке) — тогда есть деление на вкладки
    hasKinds() {
      return this.publishedCards.some((c) => c.nomenclature != null);
    },
    visibleTiles() {
      const q = (this.search || '').trim().toLowerCase();
      return this.tiles
        .filter((t) => !this.hasKinds || this.notBoughtOnly || (this.kind === 'tests' ? t.isTest : !t.isTest))
        .filter((t) => {
          if (this.boughtFilter === 'bought') return ['available', 'ended'].includes(t.state);
          if (this.boughtFilter === 'not_bought') return !['available', 'ended'].includes(t.state);
          return true;
        })
        .filter((t) => !q || `${t.card.name} ${t.card.short_description || ''}`.toLowerCase().includes(q))
        .sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state));
    },
    // Во вкладке «Все» — два блока: сверху услуги с остатком (по убыванию), в том числе закончившиеся,
    // ниже — то, что можно купить. Кошельки с нулём остаются в первом блоке.
    sections() {
      const tiles = this.visibleTiles;
      if (this.isAdmin || this.boughtFilter !== 'all') return [{ key: 'all', title: '', tiles }];
      const own = tiles
        .filter((t) => OWN_STATES.includes(t.state))
        .sort((a, b) => (b.balance === null ? -1 : b.balance) - (a.balance === null ? -1 : a.balance)
          || STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state));
      const rest = tiles.filter((t) => !OWN_STATES.includes(t.state));
      const split = own.length > 0 && rest.length > 0;
      const titles = this.isManager ? ['Ваши остатки', 'Не куплено компанией'] : ['Мои услуги', 'Доступные к покупке'];
      return [
        { key: 'own', title: split ? titles[0] : '', tiles: own },
        { key: 'rest', title: split ? titles[1] : '', tiles: rest },
      ].filter((s) => s.tiles.length);
    },
    stateHint() {
      if (this.isCorp) return 'Купленные услуги и остатки компании (в буфере и у менеджеров). Остальные можно купить.';
      if (this.isManager) return 'Ваши остатки. Недостающие услуги можно заказать через корзину.';
      return 'Просмотр каталога. Остатки компаний — в карточках корп. клиентов.';
    },
  },
  methods: {
    ...mapActions({
      bannerLink: 'users/admins/bannerLink',
      GET_ALL_SERVICES: 'services/services/GET_ALL_SERVICES',
      addToBasket: 'basket/basket/addToBasket',
      fetchUserBasket: 'basket/basket/fetchUserBasket',
    }),
    // Остаток теста для плитки или null, если показать нечего (не тест, нет данных).
    balanceOf(card) {
      if (card.nomenclature == null) return { balance: null, buffer: null, known: false };
      if (this.isCorp && this.wallet) return walletBalanceOf(card, this.wallet);
      if (this.isManager && this.managerStock) {
        const code = this.nomenclatureCodes[card.nomenclature];
        const row = code && this.managerStock.find((d) => d.code === code);
        if (!row) return { balance: null, buffer: null, known: false };
        return { balance: Number(row.amount) || 0, buffer: null, known: true };
      }
      return { balance: null, buffer: null, known: false };
    },
    buildTile(card) {
      const isTest = card.nomenclature != null;
      const { balance, buffer, known } = this.balanceOf(card);
      const paid = this.paidCardIds.includes(card.id);
      const pending = this.pendingCardIds.includes(card.id);
      const state = tileState({
        isAdmin: this.isAdmin,
        isManager: this.isManager,
        inBasket: !!this.itemInBasket(card.id),
        isTest,
        balance,
        known,
        paid,
        pending,
      });
      return {
        card,
        isTest,
        state,
        balance: this.isAdmin ? null : balance,
        bufferBalance: this.isCorp ? buffer : null,
      };
    },
    async loadOrders() {
      // Состояние по заказам нужно корпу; админ каталог только смотрит, менеджер видит остатки
      if (!this.isCorp) return;
      this.loadingOrders = true;
      try {
        const { paid, pending } = await loadOrderedCardIds();
        this.paidCardIds = paid;
        this.pendingCardIds = pending;
      } catch (e) {
        // Без заказов плитки покажут состояние по остаткам — каталог остаётся рабочим.
      } finally {
        this.loadingOrders = false;
      }
    },
    async loadBalances() {
      this.loadingBalances = true;
      try {
        if (this.isCorp) {
          const { from, to } = walletPeriodRange('30d');
          await this.$store.dispatch('users/clients/loadClientWallet', {
            id: this.user.id, from, to, self: true,
          });
        } else if (this.isManager) {
          const [stock, nomenclatures] = await Promise.all([
            this.$store.dispatch('users/counters/getStockBalance', { toType: 'manager', toId: this.user.id }),
            api.get('/stock/nomenclatures/'),
          ]);
          this.managerStock = stock && Array.isArray(stock.balance_detailed) ? stock.balance_detailed : null;
          const list = Array.isArray(nomenclatures) ? nomenclatures : (nomenclatures.results || []);
          this.nomenclatureCodes = list.reduce((acc, n) => ({ ...acc, [n.id]: n.code }), {});
        }
      } catch (e) {
        // Без остатков плитки покажут состояние по заказам.
      } finally {
        this.loadingBalances = false;
      }
    },
    async addToCart(id) {
      if (this.addingId) return;
      this.addingId = id;
      try {
        await this.addToBasket({ service_card_id: id, quantity: 1 });
        await this.fetchUserBasket();
      } catch (e) {
        this.$toast.error('Не удалось добавить в корзину', '', { position: 'topRight' });
      } finally {
        this.addingId = null;
      }
    },
    goToDetail(id) {
      this.$router.push({ name: 'serviceDetail', params: { id } });
    },
    goToLink() {
      window.open('https://t.me/Team_Assessment', '_blank');
    },
  },
  mounted() {
    this.bannerLink();
    this.GET_ALL_SERVICES();
    this.loadOrders();
    this.loadBalances();
  },
};
</script>

<style scoped lang="scss">
.listServices {
  .catalog-main {
    flex: 1 1 auto;
    min-width: 0;

    // Баннер должен стоять сразу справа от четвёртой плитки (4 × 280px + 3 × 16px), а не у края экрана
    @media (min-width: 1264px) {
      &--with-banner {
        flex: 0 1 1168px;
      }
    }
  }

  // Баннер справа от плиток: ровная колонка с отступом, не уезжает к краю и остаётся на виду при прокрутке
  .banner_preview {
    flex: 0 0 320px;
    max-width: 320px;
    margin-left: 24px;
    align-self: flex-start;
    position: sticky;
    top: 16px;

    ::v-deep .v-image {
      width: 100%;
      max-width: 100% !important;
      border: 1px solid #e0e0e0;
      border-radius: 10px;
      background: #fff;
    }
  }
}

.tiles-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
}

.tiles-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
</style>
